// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import type { ChannelPageLoaderData } from '@/pages/server/channel/channel-page';
import type { ServerPageLoaderData } from '@/pages/server/server-page';
import { API_BASE } from './config';
import ErrorBoundary from '@/components/error-boundary';
import type { UpgradePageLoaderData } from '@/pages/channels/@me/boost/upgrade-page';
import type { EditEventPageLoaderData } from '@/pages/server/events/edit-event-page';
import type { EventsPageLoaderData } from '@/pages/server/events/events-page';
import type { GamesDBPageLoaderData } from '@/pages/channels/@me/gamesdb-page';
import { MePageLoaderData } from '@/pages/channels/@me/me-dm-page';
import type { Conversation } from '@/models';
import SettingsModal from '@/components/modals/settings-modal';
import { authGuard, withAuthGuard } from '@/middleware/auth-guard';
import type { PreviewPageLoaderData } from '@/pages/server/channel/preview/preview-page';
import { withMemberGuard } from '@/middleware/member-guard.ts';
import { withRoleGuard } from '@/middleware/role-guard.ts';
import { getCurrentUser } from '@/stores/auth-store.ts';
import { getUserData } from '@/context/auth-context/auth-utils.ts';
import { RootLayout } from '@/root-layout.tsx';
import { AdminPanel } from '@/components/notifications/admin-panel.tsx';
import { PublicLayout } from '@/public-layout.tsx';
import { InviteLoaderData } from '@/pages/invite/invite-page.tsx';
import { getDefaultTextChannel } from './components/server/get-default-text-channel';

export const DEFAULT_LOGIN_REDIRECT = '/';

const routes: RouteObject[] = [
  // ---------------------------
  // PRIVATE ROUTES (auth required)
  // ---------------------------
  {
    element: <RootLayout />, // here we check user + redirect to /auth/login
    children: [
      {
        path: '',
        async lazy() {
          const { MainPage } = await import('./pages/main/main-page');
          return {
            element: <MainPage />,
            errorElement: <ErrorBoundary />,
          };
        },
        loader: withAuthGuard(async () => {
          const response = await fetch(`${API_BASE}/api/discovery`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const servers = await response.json();
          return { servers: servers };
        }),
      },
      {
        path: 'invite/:inviteCode',
        id: 'invite',
        loader: async ({ params }): Promise<InviteLoaderData> => {
          const res = await fetch(`${API_BASE}/api/invite/${params.inviteCode}/info`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!res.ok) {
            return { server: null, error: 'Invite not found' };
          }
          const server = await res.json();
          return {
            server,
            error: null,
          };
        },
        async lazy() {
          const { InvitePage } = await import('./pages/invite/invite-page');
          return {
            element: <InvitePage />,
            errorElement: <ErrorBoundary />,
          };
        },
      },
      {
        path: 'auth',
        async lazy() {
          const { AuthLayout } = await import('./pages/auth/auth-layout');
          return {
            element: <AuthLayout />,
            errorElement: <ErrorBoundary />,
          };
        },
        children: [
          {
            index: true,
            element: <Navigate to="login" replace />, // @experimental redirect to login
          },
          {
            id: 'auth_login',
            path: 'login',
            async lazy() {
              const { LoginPage } = await import('./pages/auth/login/login-page');
              return { element: <LoginPage /> };
            },
          },
          {
            id: 'auth_register',
            path: 'register',
            async lazy() {
              const { RegisterPage } = await import('./pages/auth/register/register-page');
              return { element: <RegisterPage /> };
            },
          },
          {
            id: 'auth_forgot_password',
            path: 'forgot-password',
            async lazy() {
              const { ForgotPasswordPage } = await import(
                './pages/auth/forgot-password/forgot-password-page'
              );
              return { element: <ForgotPasswordPage /> };
            },
          },
          {
            id: 'auth_reset_password',
            path: 'reset-password',
            async lazy() {
              const { ResetPasswordPage } = await import(
                './pages/auth/reset-password/reset-password-page'
              );
              return { element: <ResetPasswordPage /> };
            },
          },
          {
            id: 'auth_verify_email',
            path: 'verify-email',
            async lazy() {
              const { VerifyEmailPage } = await import(
                './pages/auth/verify-email/verify-email-page'
              );
              return { element: <VerifyEmailPage /> };
            },
          },
          {
            id: 'auth_new_verification',
            path: 'new-verification',
            async lazy() {
              const { NewVerificationPage } = await import(
                './pages/auth/new-verification/new-verification-page'
              );
              return { element: <NewVerificationPage /> };
            },
          },
          {
            id: 'auth_sign_in_credentials',
            path: 'credentials',
            async lazy() {
              const { CredentialsPage } = await import('./pages/auth/credentials/credentials-page');
              return { element: <CredentialsPage /> };
            },
          },
        ],
      },
      // TODO: secure with token?
      {
        path: 'admin/notifications',
        element: <AdminPanel />,
      },
      {
        id: 'account',
        path: 'account',
        async lazy() {
          const { AccountPage } = await import('./pages/channels/@me/account');
          return {
            element: <AccountPage />,
            errorElement: <ErrorBoundary />,
          };
        },
        loader: authGuard,
      },
      {
        id: 'discovery',
        path: 'discovery',
        async lazy() {
          const { DiscoveryPage } = await import('./pages/discovery/discovery-page');
          return {
            element: <DiscoveryPage />,
            errorElement: <ErrorBoundary />,
          };
        },
        loader: withAuthGuard(async () => {
          const response = await fetch(`${API_BASE}/api/discovery`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const servers = await response.json();
          return {
            servers: servers,
          };
        }),
      },
      {
        id: 'server_root',
        path: 'servers/:serverId',
        async lazy() {
          const { ServerPage } = await import('./pages/server/server-page');
          return {
            element: <ServerPage />,
            errorElement: <ErrorBoundary />,
          };
        },
        loader: withMemberGuard(async ({ params }): Promise<ServerPageLoaderData> => {
          const serverResponse = await fetch(`${API_BASE}/api/servers/${params.serverId}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const server = await serverResponse.json();
          // TODO: first channel should be general
          const firstChannel = getDefaultTextChannel(server.channels);
          // if (!firstChannel) {
          //   navigate('/', { replace: true });
          //   return;
          // }
          return {
            serverId: params.serverId ?? 'general',
            server: server,
            channel: firstChannel,
            members: server.members,
          };
        }),
        children: [
          {
            id: 'channel',
            path: 'channels/:channelId',
            async lazy() {
              const { ChannelPage } = await import('./pages/server/channel/channel-page');
              return {
                element: <ChannelPage />,
                errorElement: <ErrorBoundary />,
              };
            },
            loader: withRoleGuard(async ({ params }): Promise<ChannelPageLoaderData> => {
              const channelResponse = await fetch(
                `${API_BASE}/api/servers/${params.serverId}/channels/${params.channelId}`,
                {
                  method: 'GET',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                }
              );
              const { channel, server, members } = await channelResponse.json();
              return { server, channel, members };
            }),
          },
          {
            id: 'channel_preview',
            path: 'channels/:channelId/preview',
            async lazy() {
              const { PreviewPage } = await import('./pages/server/channel/preview/preview-page');
              return {
                element: <PreviewPage />,
                errorElement: <ErrorBoundary />,
              };
            },
            loader: withRoleGuard(async ({ params }): Promise<PreviewPageLoaderData> => {
              const channelResponse = await fetch(
                `${API_BASE}/api/servers/${params.serverId}/channels/${params.channelId}`,
                {
                  method: 'GET',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                }
                // 5 * 60_000
              );

              const { channel, server, members } = await channelResponse.json();
              return { server, channel, members };
            }),
          },
          {
            id: 'events',
            path: 'events',
            async lazy() {
              const { EventsPage } = await import('./pages/server/events/events-page');
              return {
                element: <EventsPage />,
                errorElement: <ErrorBoundary />,
              };
            },
            loader: withAuthGuard(async ({ params }): Promise<EventsPageLoaderData> => {
              const response = await fetch(`${API_BASE}/api/servers/${params.serverId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              const server = await response.json();
              return {
                server: server,
                events: [],
              };
            }),
          },
          {
            id: 'servers_serverId_events_eventId_edit',
            path: 'events/:eventId/edit',
            async lazy() {
              const { EditEventPage } = await import('./pages/server/events/edit-event-page');
              return {
                element: <EditEventPage />,
                errorElement: <ErrorBoundary />,
              };
            },
            loader: withAuthGuard(async ({ params }): Promise<EditEventPageLoaderData> => {
              const eventsResponse = await fetch(
                `${API_BASE}/api/servers/${params.serverId}/events/${params.eventId}`,
                {
                  method: 'GET',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                }
              );
              //  TODO: filter event from server, and get rid of 2 calls
              const serverResponse = await fetch(`${API_BASE}/api/servers/${params.serverId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              const event = await eventsResponse.json();
              const server = await serverResponse.json();
              return {
                event: event,
                server: server,
              };
            }),
          },
        ],
      },
      {
        id: 'channels_me',
        path: 'channels/@me',
        async lazy() {
          const { MePage } = await import('./pages/channels/@me/me-page');
          return {
            element: <MePage />,
            errorElement: <ErrorBoundary />,
          };
        },
        loader: withAuthGuard(async () => {
          const response = await fetch(`${API_BASE}/api/discovery`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const servers = await response.json();
          return { servers: servers };
        }),
        // loader: authGuard,
        children: [
          {
            id: 'channels_me_userId',
            path: ':userId',
            async lazy() {
              const { MeDmPage } = await import('@/pages/channels/@me/me-dm-page');
              return {
                element: <MeDmPage />,
                errorElement: <ErrorBoundary />,
              };
            },
            loader: withAuthGuard(async ({ params }): Promise<MePageLoaderData> => {
              // THIS IS OTHER USER ID
              const { userId } = params;
              if (!userId) {
                throw new Response('UserId is required', { status: 400 });
              }
              // get all conversations with OTHER user by userId
              const response = await fetch(`${API_BASE}/api/conversations/user/${userId}/dms`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
              });

              if (response.status === 500) {
                return {
                  conversation: null,
                  error: 'You cannot start a conversation with yourself.',
                };
              }

              if (response.status === 400) {
                return {
                  conversation: null,
                  error: 'Общение через личные сообщения доступно только при активной подписке.',
                };
              }
              if (!response.ok) {
                throw new Response('Conversation not found', { status: 404 });
              }

              const conversation: Conversation = await response.json();

              return {
                conversation,
              };
            }),
          },
          {
            id: 'message_requests',
            path: 'message-requests',
            async lazy() {
              const { MessageRequestsPage } = await import(
                './pages/channels/@me/message-requests-page'
              );
              return {
                element: <MessageRequestsPage />,
                errorElement: <ErrorBoundary />,
              };
            },
            loader: withAuthGuard(async () => {
              const user = getCurrentUser() ?? getUserData();
              if (!user?.id) throw new Response('Unauthorized', { status: 401 });

              const response = await fetch(`${API_BASE}/api/conversations`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
              });

              if (response.status === 400) {
                return {
                  conversationsReceived: null,
                  activeConversations: null,
                  error: 'Запросы доступны только при активной подписке.',
                };
              }

              if (!response.ok) throw new Response('Failed to load requests', { status: 500 });

              const data = await response.json();

              return {
                conversationsReceived: data.requests ?? [],
                activeConversations: data.active ?? [],
              };
            }),
          },
          {
            id: 'friends',
            path: 'friends',
            async lazy() {
              const { FriendsPage } = await import('./pages/channels/@me/friends-page');
              return {
                element: <FriendsPage />,
                errorElement: <ErrorBoundary />,
              };
            },
          },
          {
            id: 'friend_requests',
            path: 'friend-requests',
            async lazy() {
              const { FriendRequestsPage } = await import(
                './pages/channels/@me/friend-requests-page'
              );
              return {
                element: <FriendRequestsPage />,
                errorElement: <ErrorBoundary />,
              };
            },
          },
          {
            element: <SettingsModal />,
            children: [
              {
                id: 'channels_me_account',
                path: 'account',
                async lazy() {
                  const { AccountPage } = await import('./pages/channels/@me/account');
                  return {
                    element: <AccountPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'channels_me_change_password',
                path: 'change-password',
                async lazy() {
                  const { ChangePasswordPage } = await import(
                    './pages/channels/@me/change-password-page'
                  );
                  return {
                    element: <ChangePasswordPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'channels_me_devices',
                path: 'devices',
                async lazy() {
                  const { DevicesPage } = await import('./pages/channels/@me/devices-page');
                  return {
                    element: <DevicesPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'channels_me_appearance',
                path: 'appearance',
                async lazy() {
                  const { AppearancePage } = await import('./pages/channels/@me/appearance-page');
                  return {
                    element: <AppearancePage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'channels_me_notifications',
                path: 'notifications',
                async lazy() {
                  const { NotificationsPage } = await import(
                    './pages/channels/@me/notifications-page'
                  );
                  return {
                    element: <NotificationsPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'channels_me_privacy',
                path: 'privacy',
                async lazy() {
                  const { PrivacyPage } = await import('./pages/channels/@me/privacy-page');
                  return {
                    element: <PrivacyPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'channels_me_activity_privacy',
                path: 'activity-privacy',
                async lazy() {
                  const { ActivityPrivacyPage } = await import(
                    './pages/channels/@me/activity-privacy-page'
                  );
                  return {
                    element: <ActivityPrivacyPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'channels_me_language',
                path: 'language',
                async lazy() {
                  const { LanguagePage } = await import('./pages/channels/@me/language-page');
                  return {
                    element: <LanguagePage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'voice_video',
                path: 'voice-video',
                async lazy() {
                  const { VoiceVideoPage } = await import('./pages/channels/@me/voice-video-page');
                  return {
                    element: <VoiceVideoPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'channels_me_games_library',
                path: 'games-library',
                async lazy() {
                  const { GamesLibraryPage } = await import(
                    './pages/channels/@me/games-library-page.tsx'
                  );
                  return {
                    element: <GamesLibraryPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              // main
              {
                id: 'boost',
                path: 'boost',
                async lazy() {
                  const { UpgradePage } = await import('@/pages/channels/@me/boost/upgrade-page');
                  return {
                    element: <UpgradePage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
                loader: withAuthGuard(async (): Promise<UpgradePageLoaderData> => {
                  const productsResponse = await fetch(`${API_BASE}/api/products`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                  });

                  const { products } = await productsResponse.json();
                  return {
                    products: products,
                  };
                }),
              },
              // reserved, not used?
              // TODO: remove
              {
                id: 'upgrade',
                path: 'upgrade',
                async lazy() {
                  const { UpgradePage } = await import('@/pages/channels/@me/boost/upgrade-page');
                  return {
                    element: <UpgradePage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
                loader: withAuthGuard(async (): Promise<UpgradePageLoaderData> => {
                  const productsResponse = await fetch(`${API_BASE}/api/products`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                  });

                  const { products } = await productsResponse.json();
                  return {
                    products: products,
                  };
                }),
              },
              // reserved - devices/ userButton
              // TODO: remove
              {
                id: 'subscriptions',
                path: 'subscriptions',
                async lazy() {
                  const { UpgradePage } = await import('@/pages/channels/@me/boost/upgrade-page');
                  return {
                    element: <UpgradePage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
                loader: withAuthGuard(async (): Promise<UpgradePageLoaderData> => {
                  const productsResponse = await fetch(`${API_BASE}/api/products`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                  });

                  const { products } = await productsResponse.json();
                  return {
                    products: products,
                  };
                }),
              },
              // {
              //   id: 'advanced',
              //   path: 'advanced',
              //   async lazy() {
              //     const { AdvancedPage } = await import('./pages/channels/@me/advanced-page');
              //     return {
              //       element: <AdvancedPage />,
              //       errorElement: <ErrorBoundary />,
              //     };
              //   },
              // },
              {
                id: 'gamesdb',
                path: 'gamesdb',
                async lazy() {
                  const { GamesDBPage } = await import('./pages/channels/@me/gamesdb-page');
                  return {
                    element: <GamesDBPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
                loader: withAuthGuard(async (): Promise<GamesDBPageLoaderData> => {
                  const applicationsResponse = await fetch(`${API_BASE}/api/applications`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                  });

                  const applications = await applicationsResponse.json();
                  return {
                    applications: applications,
                  };
                }),
              },
              {
                id: 'keybinds',
                path: 'keybinds',
                async lazy() {
                  const { KeybindsPage } = await import('./pages/channels/@me/keybinds-page');
                  return {
                    element: <KeybindsPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
              {
                id: 'desktop',
                path: 'desktop',
                async lazy() {
                  const { DesktopPage } = await import('./pages/channels/@me/desktop');
                  return {
                    element: <DesktopPage />,
                    errorElement: <ErrorBoundary />,
                  };
                },
              },
            ],
          },
        ],
      },
      {
        id: 'legal_root',
        path: 'legal',
        element: <SettingsModal />,
        children: [
          {
            id: 'company_information',
            path: 'company-information',
            async lazy() {
              const { CompanyInformationPage } = await import(
                './pages/legal/company-information-page'
              );
              return {
                element: <CompanyInformationPage />,
                errorElement: <ErrorBoundary />,
              };
            },
          },
          // {
          //   id: 'privacy',
          //   path: 'privacy',
          //   async lazy() {
          //     const { PrivacyPage } = await import('./pages/legal/privacy-page');
          //     return {
          //       element: <PrivacyPage />,
          //       errorElement: <ErrorBoundary />,
          //     };
          //   },
          // },
          // {
          //   id: 'terms',
          //   path: 'terms',
          //   async lazy() {
          //     const { TermsPage } = await import('./pages/legal/terms-page');
          //     return {
          //       element: <TermsPage />,
          //       errorElement: <ErrorBoundary />,
          //     };
          //   },
          // },
        ],
      },
      {
        path: 'scan',
        async lazy() {
          const { OnlineScanner } = await import('./pages/scan/online-scanner');
          return {
            element: <OnlineScanner />,
          };
        },
      },
      {
        path: 'security',
        async lazy() {
          const { OnlineScanner } = await import('./pages/scan/online-scanner');
          return {
            element: <OnlineScanner />,
          };
        },
      },
      {
        path: '*',
        async lazy() {
          const { NotFoundPage } = await import('./pages/not-found-page/not-found-page');
          return {
            element: <NotFoundPage />,
          };
        },
      },
    ],
  },
  // ---------------------------
  // PUBLIC ROUTES (no auth)
  // ---------------------------
  // {
  //   element: <PublicLayout />,
  //   children: [
  //     {
  //       id: 'invite',
  //       path: 'invite/:inviteCode',
  //       loader: async ({ params }): Promise<InviteLoaderData> => {
  //         const res = await fetch(`${API_BASE}/api/invite/${params.inviteCode}/info`, {
  //           method: 'GET',
  //           credentials: 'include',
  //           headers: { 'Content-Type': 'application/json' },
  //         });
  //         if (!res.ok) {
  //           return { server: null, error: 'Invite not found' };
  //         }
  //         const server = await res.json();
  //         return {
  //           server,
  //           error: null
  //         };
  //       },
  //       async lazy() {
  //         const { InvitePage } = await import('./pages/invite/invite-page');
  //         return {
  //           element: <InvitePage />,
  //           errorElement: <ErrorBoundary />,
  //         };
  //       },
  //     },
  //   ],
  // },
];

export const router = createBrowserRouter(routes);
